/*
 * Add-to-cart form. Derived from Shopify Dawn (MIT) with two additions:
 *  - handleSubmit(event, button): public entry point used by the sticky bar and the
 *    "SectionAtcBtn-*" buttons in other sections; the spinner shows on the clicked button.
 *  - Bundle support: when a sibling <quantity-breaks> reports more than one distinct variant,
 *    the items are posted to /cart/add.js as a multi-line add.
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('.main-atc__label__text') || this.submitButton.querySelector('span');

        if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.handleSubmit(evt, this.submitButton);
      }

      handleSubmit(evt, button) {
        if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
        const activeButton = button || this.submitButton;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        activeButton.setAttribute('aria-disabled', true);
        activeButton.classList.add('loading');
        const spinner = activeButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const bundleItems = this.getBundleItems();

        if (bundleItems && bundleItems.length > 1) {
          config.body = JSON.stringify({
            items: bundleItems,
            sections: sections,
            sections_url: window.location.pathname,
          });
        } else {
          delete config.headers['Content-Type'];
          if (bundleItems && bundleItems.length === 1) {
            formData.set('id', bundleItems[0].id);
            formData.set('quantity', bundleItems[0].quantity);
          }
          if (this.cart) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              });
            }
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            activeButton.classList.remove('loading');
            activeButton.removeAttribute('aria-disabled');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      getBundleItems() {
        const section = this.closest('.shopify-section') || document;
        const breaks = section.querySelector('quantity-breaks');
        if (!breaks || typeof breaks.getItems !== 'function') return null;
        return breaks.getItems();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
